import React from 'react';
import { Icons } from '@ohif/ui-next';

interface FormSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  required?: boolean;
  options: { value: string; label: string }[];
  placeholder?: string;
  error?: string;
}

const FormSelect: React.FC<FormSelectProps> = ({
  label,
  required = false,
  options,
  placeholder = 'Select',
  error,
  className = '',
  ...props
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        width: '100%',
      }}
    >
      {/* Label */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          position: 'relative',
        }}
      >
        <label
          style={{
            fontSize: '14px',
            fontWeight: 500,
            color: 'var(--label-color-default)',
            lineHeight: '20px',
          }}
        >
          {label}
        </label>
        {required && (
          <span
            style={{
              color: '#dc2626',
              fontSize: '10px',
              position: 'absolute',
              left: '-8px',
              top: '2px',
              fontWeight: 600,
            }}
          >
            *
          </span>
        )}
      </div>

      {/* Select Field */}
      <div style={{ position: 'relative', width: '100%' }}>
        <select
          style={
            {
              width: '100%',
              height: '36px',
              padding: '8px 32px 8px 12px',
              backgroundColor: props.disabled
                ? 'var(--bg-input-disabled)'
                : 'var(--bg-input-default)',
              background: props.disabled ? 'var(--bg-input-disabled)' : 'var(--bg-input-default)',
              border: `1px solid ${error ? '#dc2626' : props.disabled ? 'var(--border-input-disabled)' : 'var(--border-input-default)'}`,
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 400,
              color: '#E2E8F0', // Use #E2E8F0 for better visibility (both enabled and disabled)
              outline: 'none',
              appearance: 'none',
              cursor: props.disabled ? 'not-allowed' : 'pointer',
              pointerEvents: props.disabled ? 'none' : 'auto',
              boxShadow: 'inset 0px 2px 4px 0px rgba(0,0,0,0.06)',
            } as React.CSSProperties
          }
          className={className}
          onFocus={e => {
            if (!props.disabled) {
              e.currentTarget.style.borderColor = 'var(--accent-primary)';
              // Maintain text color on focus to prevent browser from applying black text
              e.currentTarget.style.color = '#E2E8F0';
            }
          }}
          onBlur={e => {
            if (props.disabled) {
              e.currentTarget.style.borderColor = 'var(--border-input-disabled)';
              e.currentTarget.style.border = '1px solid var(--border-input-disabled)';
              e.currentTarget.style.backgroundColor = 'var(--bg-input-disabled)';
              e.currentTarget.style.background = 'var(--bg-input-disabled)';
              e.currentTarget.style.color = '#E2E8F0'; // Use #E2E8F0 for better visibility
            } else {
              e.currentTarget.style.borderColor = 'var(--border-input-default)';
              e.currentTarget.style.border = '1px solid var(--border-input-default)';
              e.currentTarget.style.backgroundColor = 'var(--bg-input-default)';
              e.currentTarget.style.background = 'var(--bg-input-default)';
            }
          }}
          onChange={props.disabled ? undefined : props.onChange}
          onClick={
            props.disabled
              ? e => {
                  e.preventDefault();
                  e.stopPropagation();
                }
              : undefined
          }
          {...props}
        >
          {placeholder && (
            <option
              value=""
              disabled
            >
              {placeholder}
            </option>
          )}
          {options.map(option => (
            <option
              key={option.value}
              value={option.value}
            >
              {option.label}
            </option>
          ))}
        </select>
        <div
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Icons.DownArrowIcon
            width="20"
            height="20"
            style={{ color: props.disabled ? '#64748b' : '#334155' }}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <span
          style={{
            fontSize: '12px',
            color: '#dc2626',
            marginTop: '4px',
          }}
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default FormSelect;
